/**
 * Reaktiivinen tilakartta (AB-WP2.1): komponentti-id → slot-objekti,
 * plus nimiavaruudet (vars, params). Tilaajat rekisteröityvät
 * "id.slot"-avaimille — täsmälleen niille riippuvuuksille jotka
 * {@link collectDeps} lausekkeesta keräsi.
 */

import type { ExprState } from './expression';

export type SlotListener = (depKey: string) => void;

export interface Store {
  /** Evaluointitila lausekkeille (luetaan suoraan, ei kopiota). */
  state: ExprState;
  /** Kirjoita slot ja notifioi "id.slot"-tilaajat jos arvo muuttui. */
  setSlot(id: string, slot: string, value: unknown): void;
  getSlot(id: string, slot: string): unknown;
  /** Tilaa yksi dep-avain ("id.slot"). Palauttaa unsubscribe-funktion. */
  subscribe(depKey: string, listener: SlotListener): () => void;
}

export function createStore(): Store {
  const state: ExprState = {};
  const listeners = new Map<string, Set<SlotListener>>();

  function slotsOf(id: string): Record<string, unknown> {
    let slots = state[id] as Record<string, unknown> | undefined;
    if (!slots || typeof slots !== 'object') {
      slots = {};
      state[id] = slots;
    }
    return slots;
  }

  return {
    state,
    setSlot(id, slot, value) {
      const slots = slotsOf(id);
      if (slots[slot] === value) return;
      slots[slot] = value;
      const key = `${id}.${slot}`;
      const subs = listeners.get(key);
      if (subs) {
        // Kopio ennen iterointia — listener voi rekisteröidä lisää.
        for (const fn of [...subs]) fn(key);
      }
    },
    getSlot(id, slot) {
      const slots = state[id] as Record<string, unknown> | undefined;
      return slots && typeof slots === 'object' ? slots[slot] : undefined;
    },
    subscribe(depKey, listener) {
      let subs = listeners.get(depKey);
      if (!subs) {
        subs = new Set();
        listeners.set(depKey, subs);
      }
      subs.add(listener);
      return () => subs?.delete(listener);
    },
  };
}
